import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AUTH_TOKEN_KEY,
  canEditTeamInfo,
  fetchInvoiceSettings,
  fetchSystemUserInfo,
  fetchTeamEmailTemplates,
  postSaveInvoiceInfo,
  postSetTeamEmailTemplate,
  type InvoiceCountryConfig,
  type InvoiceFormField,
  type TeamEmailTemplate,
} from '@/api';
import { FormSummaryMessage, InlineFieldError } from '@/components/ui/FormFeedback';
import { GooglePlacesAutocomplete } from '@/components/ui/GooglePlacesAutocomplete';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { teamCountryToPlacesCode } from '@/constants/teamCountries';
import { validateTeamPhone } from '@/lib/teamPhone';
import { InvoicePreviewModal } from '@/pages/team/settings/components/InvoicePreviewModal';

const inputBaseClass =
  'w-full h-8 px-2.5 text-xs font-normal rounded-md border outline-none transition disabled:bg-gray-50 disabled:text-gray-400 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400';

function inputClass(invalid: boolean): string {
  return `${inputBaseClass} ${
    invalid
      ? 'border-red-300 bg-red-50/35 focus:border-red-400'
      : 'border-gray-200 bg-white focus:border-green-500'
  }`;
}

const SUBMIT_MESSAGE_DURATION_MS = 1500;
const EMAIL_TEMPLATE_MAX_LENGTH = 500;

function clampEmailTemplate(value: string): string {
  return value.slice(0, EMAIL_TEMPLATE_MAX_LENGTH);
}

function Field({
  id,
  label,
  required,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-normal text-gray-700 mb-1">
        {label}
        {required ? <span className="text-red-400"> *</span> : null}
      </label>
      {children}
      {error ? (
        <InlineFieldError id={`${id}-error`} message={error} />
      ) : hint ? (
        <p className="text-gray-400 text-[11px] mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

function sameFieldValue(current: string, previousDefault: string | undefined): boolean {
  const left = current.trim();
  const right = (previousDefault ?? '').trim();
  if (left === right) return true;
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  return left !== '' && right !== '' && Number.isFinite(leftNumber) && leftNumber === rightNumber;
}

function valuesForFields(
  fields: InvoiceFormField[],
  previous: Record<string, string>,
  saved: Record<string, string>,
  previousFields: InvoiceFormField[] = [],
): Record<string, string> {
  const previousDefaults = new Map(previousFields.map((item) => [item.key, item.defaultValue ?? '']));
  const next: Record<string, string> = {};
  for (const item of fields) {
    const current = previous[item.key];
    const previousDefault = previousDefaults.get(item.key);
    if (
      current !== undefined
      && current !== ''
      && !sameFieldValue(current, previousDefault)
    ) {
      next[item.key] = current;
      continue;
    }
    const stored = saved[item.key];
    if (
      stored !== undefined
      && stored !== ''
      && !sameFieldValue(stored, previousDefault)
      && previousFields.length === 0
    ) {
      next[item.key] = stored;
      continue;
    }
    next[item.key] = item.defaultValue ?? '';
  }
  return next;
}

function validateFields(
  fields: InvoiceFormField[],
  values: Record<string, string>,
  country: string,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const item of fields) {
    const value = (values[item.key] ?? '').trim();
    if (!value) {
      if (item.required) errors[item.key] = `${item.label} is required.`;
      continue;
    }
    if (item.key === 'phone' && item.type === 'tel') {
      const phoneError = validateTeamPhone(value, country, item.required);
      if (phoneError) errors[item.key] = phoneError;
      continue;
    }
    if (item.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors[item.key] = 'Please enter a valid email address.';
      continue;
    }
    if (item.type === 'number') {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        errors[item.key] = `Enter a valid ${item.label}.`;
        continue;
      }
      if ((item.min !== undefined && n < item.min) || (item.max !== undefined && n > item.max)) {
        const min = item.min ?? 0;
        const max = item.max ?? 100;
        errors[item.key] = `Enter a ${item.label.toLowerCase()} between ${min} and ${max}.`;
        continue;
      }
    }
    if (item.type === 'select' && item.options && !item.options.some((option) => option.value === value)) {
      errors[item.key] = `Select a valid ${item.label}.`;
      continue;
    }
    if (item.pattern) {
      try {
        if (!new RegExp(item.pattern, 'i').test(value)) {
          errors[item.key] = item.patternMessage ?? `Enter a valid ${item.label}.`;
        }
      } catch {
        // Ignore an invalid pattern from the server.
      }
    }
  }

  const institution = fields.find((item) => item.key === 'institution_no');
  const transit = fields.find((item) => item.key === 'transit_no');
  if (institution && transit) {
    const institutionNo = (values.institution_no ?? '').trim();
    const transitNo = (values.transit_no ?? '').trim();
    if ((institutionNo || transitNo) && !institutionNo) {
      errors.institution_no = 'Institution number must be exactly 3 digits.';
    }
    if ((institutionNo || transitNo) && !transitNo) {
      errors.transit_no = 'Transit number must be exactly 5 digits.';
    }
  }
  return errors;
}

function groupTitle(group: string): string {
  if (group !== group.toLowerCase()) return group;
  return group.charAt(0).toUpperCase() + group.slice(1);
}

export default function InvoiceSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ text: string; success: boolean } | null>(null);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [countries, setCountries] = useState<InvoiceCountryConfig[]>([]);
  const [country, setCountry] = useState('');
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<TeamEmailTemplate[]>([]);
  const [emailTemplateDrafts, setEmailTemplateDrafts] = useState<Record<number, string>>({});
  const [emailTemplatesReady, setEmailTemplatesReady] = useState(false);
  const submitMessageTimerRef = useRef<number | null>(null);

  const applyEmailTemplates = (items: TeamEmailTemplate[]) => {
    setEmailTemplates(items);
    setEmailTemplateDrafts(Object.fromEntries(items.map((item) => [item.id, item.customValue])));
    setEmailTemplatesReady(true);
  };

  const selected = countries.find((item) => item.code === country) ?? countries[0];
  const fields = useMemo(() => {
    const match = countries.find((item) => item.code === country) ?? countries[0];
    return match?.fields ?? [];
  }, [countries, country]);
  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, InvoiceFormField[]>();
    for (const item of fields) {
      const group = item.group ?? 'Invoice Details';
      if (!map.has(group)) {
        map.set(group, []);
        order.push(group);
      }
      map.get(group)?.push(item);
    }
    return order.map((group) => ({ group, fields: map.get(group) ?? [] }));
  }, [fields]);

  useEffect(() => {
    return () => {
      if (submitMessageTimerRef.current) window.clearTimeout(submitMessageTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchSystemUserInfo()
      .then((user) => {
        if (cancelled) return;
        if (!canEditTeamInfo(user)) {
          navigate('/team', { replace: true });
          return;
        }
        return Promise.all([
          fetchInvoiceSettings(),
          fetchTeamEmailTemplates()
            .then((items) => ({ ok: true as const, items }))
            .catch(() => ({ ok: false as const, items: [] as TeamEmailTemplate[] })),
        ]);
      })
      .then((result) => {
        if (cancelled || !result) return;
        const [settings, templateResult] = result;
        const nextCountries = settings.countries;
        const nextCountry = nextCountries.some((item) => item.code === settings.country)
          ? settings.country
          : (nextCountries[0]?.code ?? '');
        const nextFields = nextCountries.find((item) => item.code === nextCountry)?.fields ?? [];
        setCountries(nextCountries);
        setCountry(nextCountry);
        setSavedValues(settings.values);
        setValues(valuesForFields(nextFields, {}, settings.values));
        if (templateResult.ok) applyEmailTemplates(templateResult.items);
      })
      .catch(() => {
        if (!cancelled) navigate('/team', { replace: true });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const showSubmitMessage = (text: string, success: boolean) => {
    if (submitMessageTimerRef.current) window.clearTimeout(submitMessageTimerRef.current);
    setSubmitMessage({ text, success });
    if (success) setSaved(true);
    submitMessageTimerRef.current = window.setTimeout(() => {
      submitMessageTimerRef.current = null;
      setSubmitMessage(null);
      setSaving(false);
      if (success) setSaved(false);
    }, SUBMIT_MESSAGE_DURATION_MS);
  };

  const handleCountryChange = (nextCountry: string) => {
    const nextFields = countries.find((item) => item.code === nextCountry)?.fields ?? [];
    setCountry(nextCountry);
    setValues((prev) => valuesForFields(nextFields, prev, savedValues, fields));
    setFieldErrors({});
    setFormError('');
  };

  const updateValue = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const rejectInvalid = (errors: Record<string, string>) => {
    const active = Object.fromEntries(Object.entries(errors).filter(([, message]) => message));
    setFieldErrors(active);
    if (Object.keys(active).length === 0) return false;
    const first = fields.find((item) => active[item.key]);
    if (first) {
      requestAnimationFrame(() => document.getElementById(`invoice-field-${first.key}`)?.focus());
    }
    return true;
  };

  const openInvoicePreview = () => {
    setFormError('');
    if (rejectInvalid(validateFields(fields, values, selected?.code ?? country))) {
      setFormError('Please complete required invoice settings before previewing.');
      return;
    }
    setShowInvoicePreview(true);
  };

  const handleSave = async (e?: FormEvent) => {
    e?.preventDefault();
    setFormError('');
    if (rejectInvalid(validateFields(fields, values, selected?.code ?? country))) return;
    if (!selected) {
      setFormError('Invoice settings are not loaded yet.');
      return;
    }

    const body: Record<string, string> = { country: selected.code };
    for (const item of fields) body[item.key] = (values[item.key] ?? '').trim();

    setSaving(true);
    const result = await postSaveInvoiceInfo(body);
    if (result.error) {
      setFormError(result.message);
      showSubmitMessage(result.message, false);
      return;
    }

    if (emailTemplatesReady) {
      let currentTemplates = emailTemplates;
      for (const item of emailTemplates) {
        const draft = clampEmailTemplate((emailTemplateDrafts[item.id] ?? '').trim());
        const matchesDefault = item.defaultValue.length > 0 && draft === item.defaultValue.trim();
        const nextValue = matchesDefault ? '' : draft;
        if (nextValue === item.customValue.trim()) continue;
        const templateResult = await postSetTeamEmailTemplate(item.id, nextValue);
        if (templateResult.error) {
          applyEmailTemplates(currentTemplates);
          setFormError(templateResult.message);
          showSubmitMessage(templateResult.message, false);
          return;
        }
        currentTemplates = currentTemplates.map((row) => (
          row.id === item.id ? { ...row, customValue: nextValue } : row
        ));
      }
      try {
        applyEmailTemplates(await fetchTeamEmailTemplates());
      } catch {
        applyEmailTemplates(currentTemplates);
      }
    }

    setFieldErrors({});
    setFormError('');
    try {
      const settings = await fetchInvoiceSettings();
      const nextCountries = settings.countries;
      const nextCountry = nextCountries.some((item) => item.code === settings.country)
        ? settings.country
        : selected.code;
      const nextFields = nextCountries.find((item) => item.code === nextCountry)?.fields ?? [];
      setCountries(nextCountries);
      setCountry(nextCountry);
      setSavedValues(settings.values);
      setValues(valuesForFields(nextFields, {}, settings.values));
    } catch {
      setSavedValues(body);
    }
    showSubmitMessage(result.message || 'Invoice settings saved.', true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
          <p className="text-gray-400 text-xs mt-2">Loading invoice settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SubpageHeader
        title="Invoice Settings"
        subtitle={selected?.label ?? 'Tax and bank details'}
        onBack={() => navigate('/me')}
      />

      <form
        lang="en"
        noValidate
        onSubmit={(e) => void handleSave(e)}
        className="px-4 pt-3 space-y-2.5 pb-[calc(5.25rem+env(safe-area-inset-bottom))]"
      >
        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-semibold text-gray-800">Invoice Details</div>
            <button
              type="button"
              onClick={openInvoicePreview}
              disabled={saving || countries.length === 0}
              className="flex items-center gap-0.5 text-xs font-normal text-green-600 hover:text-green-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              Preview
              <i className="ri-arrow-right-s-line text-base leading-none" />
            </button>
          </div>
          <Field id="invoice-field-country" label="Country / Region" hint="Invoice fields follow this country or region.">
            <select
              id="invoice-field-country"
              value={selected?.code ?? ''}
              onChange={(e) => handleCountryChange(e.target.value)}
              disabled={saving || countries.length === 0}
              className={`${inputClass(false)} cursor-pointer`}
            >
              {countries.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {groups.map((section) => (
          <div key={`${selected?.code ?? ''}-${section.group}`} className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
            <div className="text-xs font-semibold text-gray-800">{groupTitle(section.group)}</div>
            {section.fields.map((item) => {
              const error = fieldErrors[item.key];
              const id = `invoice-field-${item.key}`;
              const value = values[item.key] ?? '';
              const isAddress = /address/i.test(item.key);
              return (
                <Field
                  key={`${selected?.code ?? ''}-${item.key}`}
                  id={id}
                  label={item.label}
                  required={item.required}
                  hint={item.hint}
                  error={error}
                >
                  {item.type === 'select' && item.options ? (
                    <select
                      id={id}
                      value={value}
                      onChange={(e) => updateValue(item.key, e.target.value)}
                      disabled={saving || item.readOnly}
                      aria-invalid={!!error}
                      className={`${inputClass(!!error)} cursor-pointer`}
                    >
                      <option value="">Select</option>
                      {item.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : item.type === 'textarea' ? (
                    <textarea
                      id={id}
                      value={value}
                      onChange={(e) => updateValue(item.key, item.maxLength ? e.target.value.slice(0, item.maxLength) : e.target.value)}
                      placeholder={item.placeholder}
                      disabled={saving || item.readOnly}
                      rows={3}
                      aria-invalid={!!error}
                      className={`${inputClass(!!error)} h-auto py-2 resize-none`}
                    />
                  ) : isAddress ? (
                    <GooglePlacesAutocomplete
                      id={id}
                      key={`${selected?.code ?? ''}-${item.key}`}
                      value={value}
                      onChange={(next) => updateValue(item.key, next)}
                      disabled={saving || item.readOnly}
                      country={teamCountryToPlacesCode(selected?.code ?? 'AU')}
                      placeholder={item.placeholder}
                      className={inputClass(!!error)}
                    />
                  ) : (
                    <input
                      id={id}
                      type={item.type === 'email' ? 'email' : item.type === 'tel' ? 'tel' : 'text'}
                      inputMode={item.type === 'number' ? 'decimal' : item.type === 'tel' ? 'tel' : undefined}
                      value={value}
                      onChange={(e) => updateValue(item.key, item.maxLength ? e.target.value.slice(0, item.maxLength) : e.target.value)}
                      placeholder={item.placeholder}
                      disabled={saving || item.readOnly}
                      readOnly={item.readOnly}
                      spellCheck={false}
                      aria-invalid={!!error}
                      aria-describedby={error ? `${id}-error` : undefined}
                      className={inputClass(!!error)}
                    />
                  )}
                </Field>
              );
            })}
          </div>
        ))}

        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
          <div className="text-xs font-semibold text-gray-800">Email Templates</div>
          {emailTemplatesReady && emailTemplates.length === 0 ? (
            <p className="text-[11px] text-gray-400">No email templates are available.</p>
          ) : null}
          {emailTemplatesReady
            ? emailTemplates.map((item) => {
              const draft = emailTemplateDrafts[item.id] ?? '';
              const fieldId = `email-template-${item.id}`;
              return (
                <Field
                  key={item.id}
                  id={fieldId}
                  label={item.label}
                  hint={[
                    item.description,
                    `Up to ${EMAIL_TEMPLATE_MAX_LENGTH} characters. Leave it empty and save to restore the default text.`,
                  ].filter(Boolean).join(' ')}
                >
                  <textarea
                    id={fieldId}
                    value={draft}
                    onChange={(e) => {
                      const next = clampEmailTemplate(e.target.value);
                      setEmailTemplateDrafts((prev) => ({ ...prev, [item.id]: next }));
                    }}
                    placeholder={item.defaultValue || 'Enter custom text'}
                    disabled={saving}
                    maxLength={EMAIL_TEMPLATE_MAX_LENGTH}
                    rows={4}
                    spellCheck
                    className={`${inputClass(false)} h-auto py-2 resize-none`}
                  />
                  <p className="text-right text-[11px] text-gray-400 mt-1">
                    {draft.length}/{EMAIL_TEMPLATE_MAX_LENGTH}
                  </p>
                </Field>
              );
            })
            : (
              <p className="text-[11px] text-gray-400">
                Could not load email templates. Reload the page to try again.
              </p>
            )}
        </div>

        <FormSummaryMessage message={formError} />

        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="mx-auto w-full" style={{ maxWidth: 480 }}>
            <button
              type="submit"
              disabled={saving || countries.length === 0}
              className="w-full h-11 rounded-lg bg-green-500 hover:bg-green-600 text-white font-normal text-sm cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base" />
                  Saving…
                </>
              ) : saved ? (
                <>
                  <i className="ri-check-line text-base" />
                  Saved
                </>
              ) : (
                <>
                  <i className="ri-save-line text-base" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {submitMessage && (
        <div className="fixed top-16 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
          <div className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-md shadow-lg flex items-center gap-2 max-w-[90%]">
            <i
              className={
                submitMessage.success
                  ? 'ri-checkbox-circle-fill text-green-400'
                  : 'ri-error-warning-line text-amber-400'
              }
            />
            <span className="leading-snug">{submitMessage.text}</span>
          </div>
        </div>
      )}

      <InvoicePreviewModal open={showInvoicePreview} onClose={() => setShowInvoicePreview(false)} />
    </div>
  );
}
